/**
 * Admin Calling Management - Tata Cloud Integration
 * Allows testing outbound calls, scheduling calls, and viewing call logs
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { 
  Phone, PhoneCall, PhoneOff, Settings, CheckCircle, XCircle, 
  RefreshCw, Clock, User, Baby, Loader2, AlertTriangle, Calendar,
  Plus, Play, Trash2, Edit, X
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminCallingManagement = () => {
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState('schedule'); // schedule, test, logs
  const [config, setConfig] = useState(null);
  const [logs, setLogs] = useState([]);
  const [scheduledCalls, setScheduledCalls] = useState([]);
  const [statusCounts, setStatusCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [testCallLoading, setTestCallLoading] = useState(false);
  const [testNumber, setTestNumber] = useState('');
  
  // Schedule form state
  const [showScheduleForm, setShowScheduleForm] = useState(false);
  const [scheduleLoading, setScheduleLoading] = useState(false);
  const [scheduleForm, setScheduleForm] = useState({
    destination_number: '',
    scheduled_date: '',
    scheduled_time: '',
    child_name: '',
    observer_name: '',
    purpose: 'Session Call',
    notes: ''
  });

  useEffect(() => {
    loadConfig();
    loadScheduledCalls();
    loadCallLogs();
  }, []);

  const getToken = () => localStorage.getItem('admin_token') || localStorage.getItem('principal_token');

  const loadConfig = async () => {
    try {
      const token = getToken();
      const response = await axios.get(
        `${BACKEND_URL}/api/calling/config?token=${token}`
      );
      setConfig(response.data);
    } catch (err) {
      console.error('Error loading config:', err);
    }
  };

  const loadScheduledCalls = async () => {
    try {
      const token = getToken();
      const response = await axios.get(
        `${BACKEND_URL}/api/calling/scheduled?token=${token}&limit=50`
      );
      setScheduledCalls(response.data.scheduled_calls || []);
      setStatusCounts(response.data.status_counts || {});
    } catch (err) {
      console.error('Error loading scheduled calls:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadCallLogs = async () => {
    try {
      const token = getToken();
      const response = await axios.get(
        `${BACKEND_URL}/api/calling/logs?token=${token}&limit=20`
      );
      setLogs(response.data.logs || []);
    } catch (err) {
      console.error('Error loading logs:', err);
    }
  };

  const scheduleCall = async () => {
    if (!scheduleForm.destination_number || scheduleForm.destination_number.length < 10) {
      toast({
        title: "Invalid Number",
        description: "Please enter a valid 10-digit phone number",
        variant: "destructive"
      });
      return;
    }

    if (!scheduleForm.scheduled_date || !scheduleForm.scheduled_time) {
      toast({
        title: "Missing Date/Time",
        description: "Please select date and time for the call",
        variant: "destructive"
      });
      return;
    }

    setScheduleLoading(true);
    try {
      const token = getToken();
      const scheduledTime = new Date(`${scheduleForm.scheduled_date}T${scheduleForm.scheduled_time}`).toISOString();
      
      const response = await axios.post(
        `${BACKEND_URL}/api/calling/schedule?token=${token}`,
        {
          destination_number: scheduleForm.destination_number,
          scheduled_time: scheduledTime,
          child_name: scheduleForm.child_name,
          observer_name: scheduleForm.observer_name,
          purpose: scheduleForm.purpose,
          notes: scheduleForm.notes
        }
      );

      if (response.data.success) {
        toast({
          title: "Call Scheduled!",
          description: `Call scheduled for ${new Date(scheduledTime).toLocaleString()}`,
        });
        setShowScheduleForm(false);
        setScheduleForm({
          destination_number: '',
          scheduled_date: '',
          scheduled_time: '',
          child_name: '',
          observer_name: '',
          purpose: 'Session Call',
          notes: ''
        });
        loadScheduledCalls();
      }
    } catch (err) {
      console.error('Error scheduling call:', err);
      toast({
        title: "Failed to Schedule",
        description: err.response?.data?.detail || "Failed to schedule call",
        variant: "destructive"
      });
    } finally {
      setScheduleLoading(false);
    }
  };

  const executeCall = async (callId) => {
    try {
      const token = getToken();
      const response = await axios.post(
        `${BACKEND_URL}/api/calling/scheduled/${callId}/execute?token=${token}`
      );

      if (response.data.success) {
        toast({
          title: "Call Initiated!",
          description: "The call is being connected",
        });
        loadScheduledCalls();
        loadCallLogs();
      }
    } catch (err) {
      console.error('Error executing call:', err);
      toast({
        title: "Call Failed",
        description: err.response?.data?.detail || "Failed to make call",
        variant: "destructive"
      });
    }
  };

  const cancelCall = async (callId) => {
    if (!window.confirm('Are you sure you want to cancel this scheduled call?')) return;
    
    try {
      const token = getToken();
      await axios.delete(
        `${BACKEND_URL}/api/calling/scheduled/${callId}?token=${token}`
      );

      toast({
        title: "Call Cancelled",
        description: "Scheduled call has been cancelled",
      });
      loadScheduledCalls();
    } catch (err) {
      console.error('Error cancelling call:', err);
      toast({
        title: "Error",
        description: "Failed to cancel call",
        variant: "destructive"
      });
    }
  };

  const makeTestCall = async () => {
    if (!testNumber || testNumber.length < 10) {
      toast({
        title: "Invalid Number",
        description: "Please enter a valid 10-digit phone number",
        variant: "destructive"
      });
      return;
    }

    setTestCallLoading(true);
    try {
      const token = getToken();
      const response = await axios.post(
        `${BACKEND_URL}/api/calling/test?destination_number=${testNumber}&token=${token}`
      );

      if (response.data.success) {
        toast({
          title: "Call Initiated!",
          description: "Your phone will ring first, then connect to the test number",
        });
        setTimeout(() => loadCallLogs(), 2000);
      } else {
        throw new Error(response.data.message || 'Failed to initiate call');
      }
    } catch (err) {
      console.error('Error making test call:', err);
      toast({
        title: "Call Failed",
        description: err.response?.data?.detail || err.message || "Failed to make test call",
        variant: "destructive"
      });
    } finally {
      setTestCallLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed':
      case 'answered':
        return 'text-green-600 bg-green-100';
      case 'failed':
      case 'busy':
      case 'no_answer':
      case 'missed':
        return 'text-red-600 bg-red-100';
      case 'initiated':
      case 'ringing':
      case 'scheduled':
        return 'text-blue-600 bg-blue-100';
      case 'cancelled':
        return 'text-gray-600 bg-gray-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '-';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatDateTime = (isoString) => {
    if (!isoString) return '-';
    return new Date(isoString).toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get today's date in YYYY-MM-DD format for min date
  const today = new Date().toISOString().split('T')[0];

  return (
    <div className="space-y-6">
      {/* Tab Navigation */}
      <div className="flex gap-2 border-b pb-2">
        <Button
          variant={activeTab === 'schedule' ? 'default' : 'ghost'}
          onClick={() => setActiveTab('schedule')}
          className={activeTab === 'schedule' ? 'bg-sanjaya-navy' : ''}
        >
          <Calendar className="w-4 h-4 mr-2" />
          Schedule Calls
          {statusCounts.scheduled > 0 && (
            <span className="ml-2 px-2 py-0.5 bg-orange-500 text-white text-xs rounded-full">
              {statusCounts.scheduled}
            </span>
          )}
        </Button>
        <Button
          variant={activeTab === 'test' ? 'default' : 'ghost'}
          onClick={() => setActiveTab('test')}
          className={activeTab === 'test' ? 'bg-sanjaya-navy' : ''}
        >
          <PhoneCall className="w-4 h-4 mr-2" />
          Quick Call
        </Button>
        <Button
          variant={activeTab === 'logs' ? 'default' : 'ghost'}
          onClick={() => setActiveTab('logs')}
          className={activeTab === 'logs' ? 'bg-sanjaya-navy' : ''}
        >
          <Clock className="w-4 h-4 mr-2" />
          Call Logs
        </Button>
        <Button
          variant={activeTab === 'config' ? 'default' : 'ghost'}
          onClick={() => setActiveTab('config')}
          className={activeTab === 'config' ? 'bg-sanjaya-navy' : ''}
        >
          <Settings className="w-4 h-4 mr-2" />
          Config
        </Button>
      </div>

      {/* Schedule Calls Tab */}
      {activeTab === 'schedule' && (
        <div className="space-y-4">
          {/* Schedule Form */}
          {showScheduleForm ? (
            <Card className="border-2 border-sanjaya-orange">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <Plus className="w-5 h-5" />
                    Schedule New Call
                  </CardTitle>
                  <Button variant="ghost" size="sm" onClick={() => setShowScheduleForm(false)}>
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Phone Number *</Label>
                    <Input
                      type="tel"
                      placeholder="10-digit mobile number"
                      value={scheduleForm.destination_number}
                      onChange={(e) => setScheduleForm({
                        ...scheduleForm,
                        destination_number: e.target.value.replace(/\D/g, '').slice(0, 10)
                      })}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Child Name</Label>
                    <Input
                      placeholder="Child's name"
                      value={scheduleForm.child_name}
                      onChange={(e) => setScheduleForm({...scheduleForm, child_name: e.target.value})}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Date *</Label>
                    <Input
                      type="date"
                      min={today}
                      value={scheduleForm.scheduled_date}
                      onChange={(e) => setScheduleForm({...scheduleForm, scheduled_date: e.target.value})}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Time *</Label>
                    <Input
                      type="time"
                      value={scheduleForm.scheduled_time}
                      onChange={(e) => setScheduleForm({...scheduleForm, scheduled_time: e.target.value})}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Observer Name</Label>
                    <Input
                      placeholder="Observer's name"
                      value={scheduleForm.observer_name}
                      onChange={(e) => setScheduleForm({...scheduleForm, observer_name: e.target.value})}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Purpose</Label>
                    <select
                      value={scheduleForm.purpose}
                      onChange={(e) => setScheduleForm({...scheduleForm, purpose: e.target.value})}
                      className="mt-1 w-full h-10 px-3 border rounded-md"
                    >
                      <option value="Session Call">Session Call</option>
                      <option value="Follow-up">Follow-up</option>
                      <option value="Parent Check-in">Parent Check-in</option>
                      <option value="Emergency">Emergency</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  <div className="md:col-span-2">
                    <Label>Notes</Label>
                    <Textarea
                      placeholder="Any additional notes..."
                      value={scheduleForm.notes}
                      onChange={(e) => setScheduleForm({...scheduleForm, notes: e.target.value})}
                      className="mt-1"
                      rows={2}
                    />
                  </div>
                </div>
                <div className="flex gap-2 mt-4">
                  <Button
                    onClick={scheduleCall}
                    disabled={scheduleLoading}
                    className="bg-sanjaya-orange hover:bg-sanjaya-orange/90"
                  >
                    {scheduleLoading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Scheduling...
                      </>
                    ) : (
                      <>
                        <Calendar className="w-4 h-4 mr-2" />
                        Schedule Call
                      </>
                    )}
                  </Button>
                  <Button variant="outline" onClick={() => setShowScheduleForm(false)}>
                    Cancel
                  </Button>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Button onClick={() => setShowScheduleForm(true)} className="bg-sanjaya-navy">
              <Plus className="w-4 h-4 mr-2" />
              Schedule New Call
            </Button>
          )}

          {/* Status Summary */}
          <div className="grid grid-cols-4 gap-4">
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-blue-700">{statusCounts.scheduled || 0}</p>
                <p className="text-sm text-blue-600">Scheduled</p>
              </CardContent>
            </Card>
            <Card className="bg-green-50 border-green-200">
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-green-700">{statusCounts.completed || 0}</p>
                <p className="text-sm text-green-600">Completed</p>
              </CardContent>
            </Card>
            <Card className="bg-red-50 border-red-200">
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-red-700">{statusCounts.missed || 0}</p>
                <p className="text-sm text-red-600">Missed</p>
              </CardContent>
            </Card>
            <Card className="bg-gray-50 border-gray-200">
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-gray-700">{statusCounts.cancelled || 0}</p>
                <p className="text-sm text-gray-600">Cancelled</p>
              </CardContent>
            </Card>
          </div>

          {/* Scheduled Calls List */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Scheduled Calls</CardTitle>
              <Button variant="outline" size="sm" onClick={loadScheduledCalls}>
                <RefreshCw className="w-4 h-4 mr-1" />
                Refresh
              </Button>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                </div>
              ) : scheduledCalls.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Calendar className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                  <p>No scheduled calls</p>
                  <p className="text-sm">Click "Schedule New Call" to create one</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-2 px-3 font-medium text-slate-600">Scheduled For</th>
                        <th className="text-left py-2 px-3 font-medium text-slate-600">Phone</th>
                        <th className="text-left py-2 px-3 font-medium text-slate-600">Child</th>
                        <th className="text-left py-2 px-3 font-medium text-slate-600">Purpose</th>
                        <th className="text-left py-2 px-3 font-medium text-slate-600">Status</th>
                        <th className="text-left py-2 px-3 font-medium text-slate-600">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {scheduledCalls.map((call, idx) => (
                        <tr key={call.id || idx} className="border-b hover:bg-slate-50">
                          <td className="py-2 px-3">
                            <div className="font-medium">{formatDateTime(call.scheduled_time)}</div>
                          </td>
                          <td className="py-2 px-3 font-mono text-xs">{call.destination_number}</td>
                          <td className="py-2 px-3">{call.child_name || '-'}</td>
                          <td className="py-2 px-3">{call.purpose}</td>
                          <td className="py-2 px-3">
                            <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(call.status)}`}>
                              {call.status}
                            </span>
                          </td>
                          <td className="py-2 px-3">
                            {call.status === 'scheduled' && (
                              <div className="flex gap-1">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="h-7 px-2 text-green-600 hover:bg-green-50"
                                  onClick={() => executeCall(call.id)}
                                >
                                  <Play className="w-3 h-3 mr-1" />
                                  Call Now
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="h-7 px-2 text-red-600 hover:bg-red-50"
                                  onClick={() => cancelCall(call.id)}
                                >
                                  <Trash2 className="w-3 h-3" />
                                </Button>
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Quick Call Tab */}
      {activeTab === 'test' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <PhoneCall className="w-5 h-5 text-green-600" />
              Make Quick Call
            </CardTitle>
            <CardDescription>
              Make an immediate outbound call
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-800">
                <p><strong>How it works:</strong></p>
                <p>1. The agent configured in your Tata API key will receive a call first</p>
                <p>2. Once the agent picks up, the customer number will be called</p>
                <p>3. Both parties will be connected</p>
              </div>
              <div>
                <Label htmlFor="test-number">Customer Phone Number (to call)</Label>
                <Input
                  id="test-number"
                  type="tel"
                  placeholder="Enter 10-digit mobile number"
                  value={testNumber}
                  onChange={(e) => setTestNumber(e.target.value.replace(/\D/g, '').slice(0, 10))}
                  className="mt-1"
                />
              </div>
              <Button
                onClick={makeTestCall}
                disabled={testCallLoading || !testNumber || testNumber.length < 10}
                className="bg-green-600 hover:bg-green-700 text-white"
              >
                {testCallLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Initiating Call...
                  </>
                ) : (
                  <>
                    <Phone className="w-4 h-4 mr-2" />
                    Make Call Now
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Call Logs Tab */}
      {activeTab === 'logs' && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Clock className="w-5 h-5" />
                Recent Call Logs
              </CardTitle>
              <CardDescription>
                Last 20 calls made through the system
              </CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={loadCallLogs}>
              <RefreshCw className="w-4 h-4 mr-1" />
              Refresh
            </Button>
          </CardHeader>
          <CardContent>
            {logs.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <PhoneOff className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                <p>No call logs yet</p>
                <p className="text-sm">Make a call to see it here</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2 px-3 font-medium text-slate-600">Type</th>
                      <th className="text-left py-2 px-3 font-medium text-slate-600">To</th>
                      <th className="text-left py-2 px-3 font-medium text-slate-600">Status</th>
                      <th className="text-left py-2 px-3 font-medium text-slate-600">Duration</th>
                      <th className="text-left py-2 px-3 font-medium text-slate-600">Time</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logs.map((log, idx) => (
                      <tr key={log.id || idx} className="border-b hover:bg-slate-50">
                        <td className="py-2 px-3">
                          <span className="px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-700">
                            {log.type || 'call'}
                          </span>
                        </td>
                        <td className="py-2 px-3 font-mono text-xs">
                          {log.destination_number || log.customer_number || '-'}
                        </td>
                        <td className="py-2 px-3">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(log.status)}`}>
                            {log.status}
                          </span>
                        </td>
                        <td className="py-2 px-3">{formatDuration(log.duration)}</td>
                        <td className="py-2 px-3 text-slate-500 text-xs">
                          {formatDateTime(log.created_at)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Config Tab */}
      {activeTab === 'config' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5" />
              Tata Cloud Calling Configuration
            </CardTitle>
            <CardDescription>
              Current telephony integration status
            </CardDescription>
          </CardHeader>
          <CardContent>
            {config ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <span className="text-sm font-medium text-slate-600">Provider</span>
                    <span className="text-sm font-semibold text-slate-900">{config.provider}</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <span className="text-sm font-medium text-slate-600">Virtual Number</span>
                    <span className="text-sm font-mono font-semibold text-slate-900">{config.virtual_number}</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <span className="text-sm font-medium text-slate-600">API Status</span>
                    {config.api_configured ? (
                      <span className="flex items-center gap-1 text-sm font-semibold text-green-600">
                        <CheckCircle className="w-4 h-4" />
                        Connected
                      </span>
                    ) : (
                      <span className="flex items-center gap-1 text-sm font-semibold text-red-600">
                        <XCircle className="w-4 h-4" />
                        Not Configured
                      </span>
                    )}
                  </div>
                </div>
                <div className="space-y-3">
                  <p className="text-sm font-medium text-slate-600 mb-2">Features</p>
                  {Object.entries(config.features || {}).map(([feature, enabled]) => (
                    <div key={feature} className="flex items-center gap-2 text-sm">
                      {enabled ? (
                        <CheckCircle className="w-4 h-4 text-green-600" />
                      ) : (
                        <XCircle className="w-4 h-4 text-gray-400" />
                      )}
                      <span className={enabled ? 'text-slate-900' : 'text-gray-400'}>
                        {feature.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AdminCallingManagement;
